/**
 * ClientErrorsPanel — admin tab that surfaces JavaScript errors
 * captured from staff sessions by the `logger.error` pipeline.
 *
 * Why this exists
 * ---------------
 * Without this, "the page froze for one staff" is a black box. With
 * this, every client-side crash carries its message, stack, URL, and
 * the user who hit it — straight to the admin's dashboard.
 *
 * UX notes
 * --------
 *  • Default view is unresolved errors only (the work queue).
 *  • Each row shows count + first/last seen so we can tell "happened
 *    twice last week" vs "happening 50 times an hour right now".
 *  • Expand a row to see the full stack trace, last URL, and the
 *    staff member who triggered the most recent occurrence.
 *  • Mark resolved → clears it from the work queue. The same error
 *    happening again will create a fresh row (the backend dedup
 *    requires `resolved: false`).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { api } from '../../lib/store';
import { toast } from 'sonner';
import {
  AlertTriangle, RefreshCw, CheckCircle2, RotateCcw, Trash2, ChevronDown, ChevronRight,
  Bug, Loader2, ExternalLink, User as UserIcon,
} from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function ErrorRow({ row, onResolve, onUnresolve, busy }) {
  const [expanded, setExpanded] = useState(false);
  const isError = row.level === 'error';
  return (
    <div
      className="rounded-xl border border-heda-line bg-white"
      data-testid={`client-error-row-${row.id}`}
    >
      <button
        type="button"
        className="w-full flex items-start gap-2 p-3 text-left rounded-xl hover:bg-heda-cream/40"
        onClick={() => setExpanded((x) => !x)}
        data-testid={`client-error-expand-${row.id}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4 mt-0.5 shrink-0 text-heda-sub" /> : <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-heda-sub" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant={isError ? 'destructive' : 'outline'}
              className={`rounded-full border-transparent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-none ${isError ? 'bg-red-50 text-red-700 hover:bg-red-50' : 'bg-amber-50 text-amber-700'}`}
            >
              {row.level.toUpperCase()}
            </Badge>
            <span className="font-medium text-sm text-heda-ink break-words">{row.message || '(no message)'}</span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-heda-sub mt-1 flex-wrap">
            <span><strong>×{row.count}</strong> occurrences</span>
            <span>· last {fmtDate(row.last_seen)}</span>
            {row.first_seen !== row.last_seen && <span>· first {fmtDate(row.first_seen)}</span>}
            {row.last_user_name && (
              <span className="inline-flex items-center gap-1">
                · <UserIcon className="w-3 h-3" /> {row.last_user_name}
                {row.last_user_role && <span className="text-heda-mute">({row.last_user_role})</span>}
              </span>
            )}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-2 border-t border-heda-line space-y-2">
          {row.stack && (
            <div>
              <p className="heda-label">Stack trace</p>
              <pre className="text-[11px] bg-heda-cream/50 border border-heda-line rounded-xl p-3 overflow-x-auto whitespace-pre-wrap max-h-60 overflow-y-auto">
                {row.stack}
              </pre>
            </div>
          )}
          {row.last_url && (
            <p className="text-[11px] text-heda-sub inline-flex items-center gap-1 break-all">
              <ExternalLink className="w-3 h-3 shrink-0" />
              <span className="font-medium">URL:</span>
              <span className="truncate">{row.last_url}</span>
            </p>
          )}
          {row.user_agent && (
            <p className="text-[11px] text-heda-mute break-all">UA: {row.user_agent}</p>
          )}
          {row.resolved && row.resolved_by && (
            <p className="text-[11px] text-emerald-700">
              Resolved by <strong>{row.resolved_by}</strong> on {fmtDate(row.resolved_at)}
            </p>
          )}
          <div className="flex gap-2 pt-1">
            {row.resolved ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onUnresolve(row.id)}
                disabled={busy}
                className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-8 text-xs"
                data-testid={`client-error-unresolve-${row.id}`}
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Reopen
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => onResolve(row.id)}
                disabled={busy}
                className="h-8 rounded-xl bg-heda-ink text-xs text-white shadow-none hover:bg-black"
                data-testid={`client-error-resolve-${row.id}`}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" /> Mark resolved
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClientErrorsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listClientErrors({ show_resolved: showResolved, limit: 200 });
      setRows(res.data.errors || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load errors');
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleResolve = async (id) => {
    setBusyId(id);
    try {
      await api.resolveClientError(id);
      toast.success('Marked as resolved');
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnresolve = async (id) => {
    setBusyId(id);
    try {
      await api.unresolveClientError(id);
      toast.success('Reopened');
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen');
    } finally {
      setBusyId(null);
    }
  };

  const handleClearResolved = async () => {
    if (!window.confirm('Permanently delete all resolved errors?')) return;
    try {
      const res = await api.clearResolvedClientErrors();
      toast.success(`Deleted ${res.data.deleted} resolved errors`);
      await fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear');
    }
  };

  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="client-errors-panel">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 border-b border-heda-line px-5 py-3">
        <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
          <Bug className="w-4 h-4 text-heda-gold" />
          Client errors
          <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-none ml-1 border-transparent bg-heda-cream text-heda-mute">
            {total}
          </Badge>
        </CardTitle>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Switch
              id="show-resolved"
              checked={showResolved}
              onCheckedChange={setShowResolved}
              data-testid="client-errors-show-resolved-toggle"
            />
            <Label htmlFor="show-resolved" className="text-[12.5px] text-heda-sub cursor-pointer">
              Include resolved
            </Label>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchRows}
            disabled={loading}
            className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-9 text-[12.5px]"
            data-testid="client-errors-refresh-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Refresh
          </Button>
          {showResolved && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleClearResolved}
              className="h-9 rounded-full border-red-200 bg-white text-[12.5px] text-red-700 shadow-none hover:bg-red-50 hover:text-red-700"
              data-testid="client-errors-clear-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Clear resolved
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-5 py-4 space-y-2">
        {!loading && rows.length === 0 && (
          <div className="flex flex-col items-center text-center py-10">
            <span className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint">
              <CheckCircle2 className="w-8 h-8 text-heda-olive" />
            </span>
            <p className="font-display font-bold text-heda-ink">No {showResolved ? '' : 'unresolved '}errors.</p>
            <p className="text-sm text-heda-mute">Everything looks healthy on the client side.</p>
          </div>
        )}
        {rows.map((row) => (
          <ErrorRow
            key={row.id}
            row={row}
            onResolve={handleResolve}
            onUnresolve={handleUnresolve}
            busy={busyId === row.id}
          />
        ))}
        {!loading && rows.length > 0 && (
          <p className="text-[11px] text-heda-mute pt-2 text-center">
            Showing newest first. Each row groups repeats of the same error (de-duped server-side).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
